import { useContext } from 'react';
import type { QueryKey } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { buildURLParamsString } from '~/helpers/api';
import apiCalls from '~/helpers/apiCalls';
import { UserContext } from '~/dashboard/context';
import {
  getFormattedPublishedAttributeGroupings,
  getFormattedPublishedAttributes,
  getPublishedAttributeGroupingsAddPayload,
  getPublishedAttributesAddPayload,
  getPublishedAttributesEditPayload,
} from './utils';
import type { PublishedAttributeFormatted, PublishedAttributeGroupingFormatted } from './types';
import { useWorkflowProps } from '../../workflows/api';

export const usePublishedAttributesList = ({
  includeSystemPAs = false,
}) => {
  const { typhonDomain, featureFlags } = useContext(UserContext);

  return useQuery({
    queryKey: ['publishedAttributes.list'],
    queryFn: async () => {
      const {
        published_attributes: publishedAttributes,
      } = await apiCalls.publishedAttributes.list({
        typhonDomain,
        queryParamsStr: buildURLParamsString({ include_system_PAs: includeSystemPAs }),
      });

      return getFormattedPublishedAttributes(publishedAttributes, featureFlags);
    },
  });
};

export const useAddPublishedAttributeMutation = ({
  invalidateKeys = [],
}: { invalidateKeys?: QueryKey[] } = {}) => {
  const { typhonDomain, featureFlags } = useContext(UserContext);
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['publishedAttributes.post'],
    mutationFn: async (payload: PublishedAttributeFormatted) => {
      const body = getPublishedAttributesAddPayload(payload, featureFlags);
      const res = await apiCalls.publishedAttributes.post({ typhonDomain, body });

      [
        ['publishedAttributes.list'],
        ['publishedAttributeGroupings.list'],
        ...invalidateKeys,
      ].forEach(keyToInvalidate => {
        queryClient.invalidateQueries({ queryKey: keyToInvalidate });
      });

      return res;
    },
  });
};

export const useEditPublishedAttributeMutation = () => {
  const { typhonDomain, featureFlags } = useContext(UserContext);
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['publishedAttributes.patch'],
    mutationFn: async (payload: Partial<PublishedAttributeFormatted>) => {
      const { attributeToken } = payload;
      const body = getPublishedAttributesEditPayload(payload, featureFlags);
      const res = await apiCalls.publishedAttributes.patch({ typhonDomain, attributeToken, body });

      return res;
    },
    // returning a Promise causes the mutation to have a isLoading state until the queries are re-fetched
    onSuccess: () => Promise.all([
      queryClient.invalidateQueries({ queryKey: ['publishedAttributes.list'] }),
      queryClient.invalidateQueries({ queryKey: ['publishedAttributeGroupings.list'] }),
      queryClient.invalidateQueries(useWorkflowProps.getQueryKey()),
    ]),
  });
};

export const usePublishedAttributeGroupingsList = () => {
  const { typhonDomain, featureFlags } = useContext(UserContext);

  return useQuery({
    queryKey: ['publishedAttributeGroupings.list'],
    queryFn: async () => {
      const {
        published_attributes_groupings: publishedAttributeGroupings,
      } = await apiCalls.publishedAttributes.groupings.list({
        typhonDomain,
      });

      return getFormattedPublishedAttributeGroupings(publishedAttributeGroupings);
    },

    enabled: Boolean(featureFlags?.groupedPublishedAttributes),
  });
};

export const useAddPublishedAttributeGroupingMutation = () => {
  const { typhonDomain } = useContext(UserContext);
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['publishedAttributeGroupings.post'],
    mutationFn: async (payload: Partial<PublishedAttributeGroupingFormatted>) => {
      const body = getPublishedAttributeGroupingsAddPayload(payload);
      const res = await apiCalls.publishedAttributes.groupings.post({ typhonDomain, body });
      return res;
    },
    onSuccess: () => {
      [
        ['publishedAttributes.list'],
        ['publishedAttributeGroupings.list'],
      ].forEach(keyToInvalidate => {
        queryClient.invalidateQueries({ queryKey: keyToInvalidate });
      });
    },
  });
};

export const useEditPublishedAttributeGroupingMutation = () => {
  const { typhonDomain } = useContext(UserContext);
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['publishedAttributeGroupings.patch'],
    mutationFn: async (payload: Partial<PublishedAttributeGroupingFormatted>) => {
      const { groupingToken } = payload;
      const body = getPublishedAttributeGroupingsAddPayload(payload);
      const res = await apiCalls.publishedAttributes.groupings.patch({
        typhonDomain,
        groupingToken,
        body,
      });

      return res;
    },
    onSuccess: () => {
      [
        ['publishedAttributes.list'],
        ['publishedAttributeGroupings.list'],
      ].forEach(keyToInvalidate => {
        queryClient.invalidateQueries({ queryKey: keyToInvalidate });
      });
    },
  });
};
